import ActionButton from '@repo/ui/action-button'
import { PenIcon, PlusIcon, TelephoneIcon } from '@repo/ui/icons'
import Modal, { MODAL_CONTENT_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import { useRef } from 'react'
import { toast } from 'sonner'

import { API, constructApiUrl } from '../../../api/apis.config'
import { createMobileNumberForFrenchLeader } from '../../../api/ols/mobile-number-for-french-leaders/create-mobile-number-for-french-leader'
import { updateMobileNumberForFrenchLeader } from '../../../api/ols/mobile-number-for-french-leaders/update-mobile-number-for-french-leade'
import DynamicDataTable, { DynamicTableRef } from '../../../components/elements/dynamic-table'
import UserNavBar from '../../../components/elements/nav-bar'
import MobileNumberForFrenchLeaderForm from '../../../components/pages/ols/mobile-number-for-french-leaders/mobile-number-for-french-leader-form'
import useAuth from '../../../lib/hooks/use-auth'
import { useModalManager } from '../../../lib/hooks/use-modal-manager'
import useTranslate from '../../../lib/hooks/use-translate'
import { MobileNumberForFrenchLeader } from '../../../lib/types/models/ol/mobile-number-for-french-leader/mobile-number-for-french-leader'
import { modalButtonConfig } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../lib/utils/functions/misc.functions'
import { getMobileNumberForFrenchLeadersColumns } from './utils'

const MobileNumberForFrenchLeadersPage = () => {
  const tableRef = useRef<DynamicTableRef<MobileNumberForFrenchLeader>>(null)
  const { user } = useAuth()
  const { t } = useTranslate()

  const { selectedData, modalState, toggleModal, closeModel } = useModalManager<
    'create' | 'update',
    MobileNumberForFrenchLeader
  >(['create', 'update'])

  const { mutateAsync: createMobileNumberForFrenchLeaderMutation } = useMutation({
    mutationFn: createMobileNumberForFrenchLeader,
    onSuccess: () => toast.success('Mobile number for French leader created successfully'),
    onError: showAxiosError
  })

  const { mutateAsync: updateMobileNumberForFrenchLeaderMutation } = useMutation({
    mutationFn: updateMobileNumberForFrenchLeader,
    onSuccess: () => {
      toast.success('Mobile number for French leader updated successfully')
      tableRef.current?.refetch()
    },
    onError: showAxiosError
  })

  const olId = user?.ol?.id

  if (!olId) return null

  return (
    <div>
      <UserNavBar title={'Mobile number for French leader'} icon={TelephoneIcon} />
      <div className={'h-4'} />
      <DynamicDataTable<MobileNumberForFrenchLeader, null>
        ref={tableRef}
        title={'Phone numbers'}
        deleteMessage={{
          attributeName: 'phoneNumber',
          name: 'phone number',
          deleteAll: 'Are you sure you want to delete the selected phone numbers?'
        }}
        columns={[
          ...getMobileNumberForFrenchLeadersColumns(t),
          {
            accessorKey: 'id',
            columnHeaderClassName: 'w-1/2 ',
            header: '',
            cell: ({ row }) => {
              return (
                <div className="flex justify-end pr-3">
                  <ActionButton
                    icon={PenIcon}
                    variant={'grey'}
                    onClick={() => toggleModal('update', row.original)}
                  />
                </div>
              )
            }
          }
        ]}
        selectable
        allowSearchParam
        endpoint={constructApiUrl(API.ols.getMobileNumberForFrenchLeaders.url, { olId: +olId })}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            variant: 'primary',
            label: 'Add mobile number',
            fn: () => toggleModal('create')
          }
        ]}
      />

      <Modal
        hideActions
        isOpen={modalState.create}
        onCancel={() => closeModel('create')}
        icon={TelephoneIcon}
        title="Add a new mobile number for the French leader"
      >
        <MobileNumberForFrenchLeaderForm
          className={MODAL_CONTENT_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('create'),
            onConfirm: async (values) => {
              await createMobileNumberForFrenchLeaderMutation({ olId, ...values })
              tableRef.current?.refetch()
              closeModel('create')
            },
            isEnglish: true
          })}
        />
      </Modal>

      {selectedData && (
        <Modal
          hideActions
          isOpen={modalState.update}
          onCancel={() => closeModel('update')}
          icon={TelephoneIcon}
          title="Update mobile number for the French leader"
        >
          <MobileNumberForFrenchLeaderForm
            className={MODAL_CONTENT_CLASSNAME}
            defaultValues={selectedData}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('update'),
              onConfirm: async (values) => {
                await updateMobileNumberForFrenchLeaderMutation({
                  id: selectedData.id,
                  olId,
                  ...values
                })
                tableRef.current?.refetch()
                closeModel('update')
              },
              isEnglish: true
            })}
          />
        </Modal>
      )}
    </div>
  )
}

export default MobileNumberForFrenchLeadersPage
